import { paraISOaPartirDeExibicao } from "../../../shared/lib/date";

// Agregações das visões do Calendário (Mês, Semana, Lista) feitas sobre a listagem de
// plantões (LinhaPlantaoDTO), para que os filtros de status e de vínculo valham igual em
// todas as visões e no resumo do mês. Somas sempre em centavos: somar floats de reais
// gera diferença de centavos entre o que se exibe e o que se soma.

const toCents = (value) => Math.round(Number(value || 0) * 100);
const fromCents = (cents) => cents / 100;
const pad = (n) => String(n).padStart(2, "0");

export function isoDate(year, month, day) {
  return `${year}-${pad(month)}-${pad(day)}`;
}

// "yyyy-MM-dd" -> Date local (meia-noite no fuso do navegador, nunca UTC).
export function parseIsoDate(iso) {
  const [year, month, day] = iso.split("-").map(Number);
  return new Date(year, month - 1, day);
}

export function toIsoDate(date) {
  return isoDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
}

export function addDays(iso, delta) {
  const date = parseIsoDate(iso);
  date.setDate(date.getDate() + delta);
  return toIsoDate(date);
}

// Data de trabalho do plantão ("dd/MM/yyyy" no DTO) como "yyyy-MM-dd". É sempre o dia em
// que o plantão começa: um noturno que vira a meia-noite continua só nesse dia.
export function shiftDate(shift) {
  return paraISOaPartirDeExibicao(shift.data);
}

// Status e vínculo se combinam (E). `vinculoIds` vazio ou ausente = todos os vínculos.
export function filterShifts(shifts, { status = "", vinculoIds = [] } = {}) {
  const vinculos = new Set(vinculoIds.map(Number));
  return shifts.filter(
    (shift) => (!status || shift.status === status) && (vinculos.size === 0 || vinculos.has(Number(shift.vinculo_id)))
  );
}

// Mesmo formato de GET /calendario (dias + resumo), montado a partir de duas listas já
// filtradas: os plantões trabalhados no mês (`workedShifts`) e os que têm pagamento
// previsto no mês (`paymentShifts`, buscados por ano_pagamento/mes_pagamento — por isso o
// dia sai de `previsao`, "dd/MM").
export function buildMonthCalendar({ year, month, workedShifts, paymentShifts, today }) {
  const lastDay = new Date(year, month, 0).getDate();
  const workedByDay = {};
  const paymentByDay = {};
  for (const shift of workedShifts) {
    const day = Number(shift.data.split("/")[0]);
    workedByDay[day] = (workedByDay[day] ?? 0) + toCents(shift.valor);
  }
  for (const shift of paymentShifts) {
    const day = Number(shift.previsao.split("/")[0]);
    paymentByDay[day] = (paymentByDay[day] ?? 0) + toCents(shift.valor);
  }

  const days = [];
  let billedCents = 0;
  for (let day = 1; day <= lastDay; day += 1) {
    billedCents += workedByDay[day] ?? 0;
    days.push({
      dia: day,
      plantao_realizado: fromCents(workedByDay[day] ?? 0),
      pagamento_previsto: fromCents(paymentByDay[day] ?? 0),
      hoje: isoDate(year, month, day) === today,
    });
  }

  return {
    days,
    summary: {
      plantoes: workedShifts.length,
      faturamento: fromCents(billedCents),
      dias_de_recebimento: Object.keys(paymentByDay).length,
    },
  };
}

// Plantões de cada data ("yyyy-MM-dd"), para visões que atravessam meses (Semana).
export function groupShiftsByDate(shifts) {
  const byDate = {};
  for (const shift of shifts) {
    const date = shiftDate(shift);
    if (!byDate[date]) byDate[date] = [];
    byDate[date].push(shift);
  }
  return byDate;
}

// Compromissos que ocupam a data (datas ISO comparam como texto).
export function eventsOnDate(events, date) {
  return events.filter((event) => event.data_inicio <= date && event.data_fim >= date);
}

const byStartTime = (a, b) => (a.hora_inicio ?? "99:99").localeCompare(b.hora_inicio ?? "99:99");

// Lista do período agrupada por dia, em ordem cronológica, só com os dias que têm algo.
// Compromisso de vários dias aparece uma vez, no primeiro dia que ocupa dentro do período
// (o período "de 25/10 a 03/11" já aparece na linha); compromisso nunca entra em total.
export function buildDayList({ shifts, events, from, to }) {
  const byDate = {};
  const dayAt = (date) => {
    if (!byDate[date]) byDate[date] = { date, shifts: [], events: [], subtotalCents: 0 };
    return byDate[date];
  };

  let totalCents = 0;
  for (const shift of shifts) {
    const day = dayAt(shiftDate(shift));
    day.shifts.push(shift);
    day.subtotalCents += toCents(shift.valor);
    totalCents += toCents(shift.valor);
  }
  for (const event of events) {
    const firstDate = event.data_inicio < from ? from : event.data_inicio;
    if (firstDate > to || event.data_fim < from) continue;
    dayAt(firstDate).events.push(event);
  }

  const days = Object.values(byDate)
    .sort((a, b) => a.date.localeCompare(b.date))
    .map(({ subtotalCents, ...day }) => ({ ...day, shifts: [...day.shifts].sort(byStartTime), subtotal: fromCents(subtotalCents) }));
  return { days, total: fromCents(totalCents), count: shifts.length };
}

// As 7 datas (domingo a sábado, como a grade do mês) da semana que contém `date`.
export function weekDates(date) {
  const sunday = addDays(date, -parseIsoDate(date).getDay());
  return Array.from({ length: 7 }, (_, i) => addDays(sunday, i));
}
